import { inject, NewInstance, BindingEngine } from 'aurelia-framework';
import { QuoteService } from 'pages/quotes/service';
import { CustomerService } from 'pages/quote-setup/customers/service';
import { ProjectService } from 'pages/quote-setup/projects/service';
import { PlantService } from 'pages/quote-setup/plants/service';
import { PartService } from 'pages/quote-setup/parts/service';
import { EventAggregator } from 'aurelia-event-aggregator';
import { DialogController } from 'aurelia-dialog';
import { DialogService } from 'aurelia-dialog';
import { ValidationRules, ValidationController, validateTrigger } from 'aurelia-validation';
import { BootstrapFormRenderer } from 'resources/validation-renderer';
import { Router, RouterEvent } from 'aurelia-router';
import toastr from 'toastr';

@inject(DialogController, NewInstance.of(ValidationController), QuoteService, EventAggregator, DialogService, CustomerService,
  ProjectService, PlantService, PartService, BindingEngine, Router)
export class QuoteAddEditor {
  constructor(controller, validationController, quoteService, ea, dialogService, customerService, projectService, plantService, 
    partService, bindingEngine, router) {
    this.controller = controller;
    this.quoteService = quoteService;
    this.ea = ea;
    this.dialogService = dialogService;
    this.projectService = projectService;
    this.customerService = customerService;
    this.plantService = plantService;
    this.partService = partService;
    this.router = router;

    this.quote = {};

    this.validationController = validationController;
    this.validationController.validateTrigger = validateTrigger.manual;
    this.validationController.addRenderer(new BootstrapFormRenderer());
    this.rules = ValidationRules
      .ensure('projectId').required().withMessage('Project is required')
      .ensure('plantId').required().withMessage('Plant is required')
      .ensure('customerId').required().withMessage('Customer is required')
      .ensure('partId').required().withMessage('Part is required')
      .rules;
    this.isValid = true;

    bindingEngine.propertyObserver(this.quote, 'projectId').subscribe(async (newValue) => {
      if (newValue != null) await this.getValidStatus();
    });

    bindingEngine.propertyObserver(this.quote, 'plantId').subscribe(async (newValue) => {
      if (newValue != null) await this.getValidStatus();
    });

    bindingEngine.propertyObserver(this.quote, 'partId').subscribe(async (newValue) => {
      if (newValue != null) await this.getValidStatus();
    });

    bindingEngine.propertyObserver(this.quote, 'customerId').subscribe(async (newValue) => {
      if (newValue != null) await this.getValidStatus();
    });

  }

  async activate(item) {
    if (item) {
      this.quote = item;
    }
  }

  addProject() {
    this.dialogService.open({ viewModel: this.projectService.editor });
  }

  addCustomer() {
    this.dialogService.open({ viewModel: this.customerService.editor });
  }

  addPlant() {
    this.dialogService.open({ viewModel: this.plantService.editor });
  }

  addPart() {
    this.dialogService.open({ viewModel: this.partService.editor, model: { clonePartId: this.quote.partId } });
  }

  async getValidStatus() {
    if (this.quote.plantId && this.quote.customerId && this.quote.projectId && this.quote.partId) {
      this.isValid = await this.quoteService.getQuoteValidStatus(this.quote.plantId, this.quote.projectId, this.quote.partId, this.quote.customerId);
      if (!this.isValid) {
        toastr.error('A quote already exists for this part combination.');
      }
    }
  }

  async addCustomerProject() {
    let customerProjectId = await this.quoteService.getCustomerProjectIdForQuote(this.quote.plantId, this.quote.projectId, this.quote.customerId);
    if (customerProjectId != 0) {
      this.quote.customerProjectId = customerProjectId;
      return customerProjectId;
    }
    else {
      let item = {
        customerId: this.quote.customerId,
        customerName: this.quote.customerName,
        projectId: this.quote.projectId,
        projectNumber: this.quote.projectName,
        plantId: this.quote.plantId,
        plantName: this.quote.plantName,
        message: "No customer project found for this quote. Submit a customer project to link it to the quote."
      };
  
      this.dialogService.open({ viewModel: this.quoteService.quoteCustomerProjectEditor, model: item })
      .whenClosed(response => {
        if (!response.wasCancelled) {
            this.quote.customerProjectId = response.output.id;
            this.ea.publish('quote-customer-project:added');
            return this.quote.customerProjectId;
          }
      });
    }
  }

  async save() {
    if (this.isRequesting) return;
    this.isRequesting = true;
    
    const validationResult = await this.validationController.validate();
    let customerProjectId = await this.addCustomerProject();
    if (validationResult.valid && this.isValid && customerProjectId > 0) {
      try {
        if (this.quote.id != null) {
          const newId = (await this.quoteService.clone(this.quote)).quoteId;
          toastr.success('Quote was cloned!');
          this.ea.publish('quotes:updated', this.quote);
          this.controller.ok(this.quote);
          this.router.navigateToRoute('quote-edit', {id: newId}, {trigger: true});
        } else {
          const newId = await this.quoteService.create(this.quote);
          toastr.success('New quote was created!');
          this.ea.publish('quotes:updated', this.quote);
          this.controller.ok(this.quote);
          this.router.navigateToRoute('quote-edit', {id: newId}, {trigger: true});
          if (this.router.currentInstruction.config.name === 'quote-edit') {
            window.location.reload();
          }
        }
        this.quote.id = 0;
      } catch {
        this.isRequesting = false;
      }
    }
    this.isRequesting = false;
  }
}
